import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

import { Icon } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";

import { workspaces as workspacesApi } from "apis/callables";
import {
    IWorkspacePermissionAccess,
    IWorkspaceResourceFolder,
    IWorkspaceResourceFolderMetadata,
    WorkspacePermissionObjectType,
    WorkspaceResourceType,
    GET_DEFAULT_RESTRICTED_PERMISSIONS_ACCESS
} from "common/interfaces";
import { Breadcrumb } from "js/Components/Breadcrumb";
import { IconButton } from "js/Components/IconButton";
import { List, ListHeaderLine, ListLine } from "js/Components/List";
import { MenuItem } from "js/Components/Menu";
import { Popup, PopupContent, PopupPreview } from "js/Components/Popup";
import WorkspaceController, {
    WorkspaceControllerState,
    isLegacyAllTeamMembersFolder,
    isLegacyUserFolder
} from "js/controllers/WorkspaceController";
import getLogger from "js/core/logger";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import {
    ShowConfirmationDialog,
    ShowDialog,
    ShowErrorDialog,
    ShowInputDialog,
    ShowWarningDialog
} from "js/react/components/Dialogs/BaseDialog";
import { RoundIconButton, UIPane } from "js/react/components/UiComponents";
import { useIntersectionObserver } from "js/react/hooks/useIntersectionObserver";
import {
    SearchBarContainer,
    UIPaneResultsContainer
} from "js/react/views/AddSlide/Panes/Components/SearchBox";
import AllTeamMembersFolderSettingsDialog from "js/react/views/TeamResources/dialogs/AllTeamMembersFolderSettingsDialog";
import { ManageTeamFolderMembersDialog } from "js/react/views/TeamResources/dialogs/TeamFolderDialogs";

const logger = getLogger();

const FolderNameContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    font-weight: 600;
`;

const StyledSkeleton = styled(Skeleton)`
    &&& {
        height: 34px;
    }
`;

const FolderIconButtonContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 60px;
`;

const StyledIconButton = styled(IconButton)`
    &&& {
        transition: opacity 0.3s ease-in-out;
        opacity: ${props => props.visible ? 1 : 0};
        width: 40px;
        height: 40px;

        &:hover {
            background: rgba(0, 0, 0, 0.03);
            border-radius: 50%;
        }
    }
`;

const PopupMenuItem = styled(MenuItem)`
    &&& {
        > .MuiIcon-root {
            margin-right: 5px;
            color: #666;
        }
    }
`;

const NoMembershipDivider = styled.div`
    font-weight: 600;
    color: #666;
    width: 100%;
    text-align: center;
`;

function FolderIconButton({ icon, visible, onClick }: { icon: string, visible: boolean, onClick?: () => void }) {
    return <StyledIconButton icon={icon} onClick={onClick} size={30} color="rgb(102,102,102)" visible={visible} />;
}

function Folder({ folder }: { folder: IWorkspaceResourceFolder }) {
    if (folder.parentFolderId) {
        return null;
    }

    const containerRef = useRef<HTMLDivElement>(null);
    const metadataLoadedAtRef = useRef<number>(0);

    const [isMouseOver, setIsMouseOver] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [metadata, setMetadata] = useState<IWorkspaceResourceFolderMetadata | null>(null);
    const [permissions, setPermissions] = useState<IWorkspacePermissionAccess>(GET_DEFAULT_RESTRICTED_PERMISSIONS_ACCESS());
    const isIntersecting = useIntersectionObserver(containerRef);

    const isDefault = isLegacyAllTeamMembersFolder(folder);
    const membersCount = metadata ? Object.values(metadata.counters.users).reduce((acc, curr) => acc + curr, 0) : 0;
    const presentationsCount = metadata ? metadata.counters.resources[WorkspaceResourceType.PRESENTATION] : 0;

    useEffect(() => {
        if (!isIntersecting) {
            return;
        }

        if (!metadata || folder.modifiedAt > metadataLoadedAtRef.current) {
            (async () => {
                try {
                    const [metadata, permissions] = await Promise.all([
                        workspacesApi.getWorkspaceResourceFolderMetadata({
                            workspaceId: folder.workspaceId,
                            resourceFolderId: folder.id
                        }),
                        workspacesApi.getUserPermissionsToResource({
                            workspaceId: folder.workspaceId,
                            resourceId: folder.id,
                            resourceType: WorkspacePermissionObjectType.RESOURCE_FOLDER
                        })
                    ]);
                    metadataLoadedAtRef.current = Date.now();
                    setMetadata(metadata);
                    setPermissions(permissions);
                    setFetching(false);
                } catch (err) {
                    logger.error(err, `Failed to fetch data for folder ${folder.id}`);
                }
            })();
        }
    }, [isIntersecting, folder.modifiedAt]);

    const handleMouseEnter = () => setIsMouseOver(true);
    const handleMouseLeave = () => setIsMouseOver(false);

    const handleOpenAllTeamMembersFolderSettings = () => {
        ShowDialog(AllTeamMembersFolderSettingsDialog, { folder });
    };

    const handleShareFolder = (closePopup: () => void) => async () => {
        closePopup();

        ShowDialog(ManageTeamFolderMembersDialog, {
            folderId: folder.id
        });
    };

    const handleRenameFolder = closePopup => async () => {
        closePopup();

        const name = await ShowInputDialog({
            title: "Rename Team Folder",
            maxChars: 50,
            value: folder.name
        });

        if (!name || name === folder.name) {
            return;
        }

        try {
            await withProgressDialog({
                title: "Renaming folder...",
                action: () => WorkspaceController.updateWorkspaceResourceFolder({ folderId: folder.id, name })
            });
        } catch (err) {
            logger.error(err, `Failed to rename folder ${folder.id}`);

            ShowErrorDialog({
                title: "Failed to rename folder",
                message: "An error occurred while renaming the folder. Please try again."
            });
        }
    };

    const handleDeleteFolder = closePopup => async () => {
        closePopup();

        const shouldDelete = await ShowConfirmationDialog({
            title: "Are you sure you want to delete this team folder?",
            message: "This action can not be undone."
        });

        if (!shouldDelete) {
            return;
        }

        try {
            await withProgressDialog({
                title: "Deleting folder...",
                action: () => WorkspaceController.deleteWorkspaceResourceFolder({ folderId: folder.id })
            });
        } catch (err) {
            logger.error(err, `Failed to delete folder ${folder.id}`);

            ShowErrorDialog({
                title: "Failed to delete folder",
                message: "An error occurred while deleting the folder. Please try again."
            });
        }
    };

    const handleDoubleClick = () => {
        if (fetching || !permissions.write.object || !permissions.write.permissions) {
            return;
        }

        if (isDefault) {
            handleOpenAllTeamMembersFolderSettings();
            return;
        }

        handleShareFolder(() => { })();
    };

    return (
        <ListLine
            ref={containerRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onDoubleClick={handleDoubleClick}
            data-test-id={`team-folder-container-${folder.id}`}
            data-test-name={folder.name}
        >
            <FolderNameContainer data-test-id="team-folder-name-container">
                <Icon>{isDefault ? "apartment" : "folder_open"}</Icon>
                {folder.name}
            </FolderNameContainer>
            {fetching ? <StyledSkeleton /> : <div data-test-id="team-folder-members-count">{membersCount} {membersCount === 1 ? "person" : "people"}</div>}
            {fetching ? <StyledSkeleton /> : <div data-test-id="team-folder-presentations-count">{presentationsCount} presentation{presentationsCount === 1 ? "" : "s"}</div>}
            {!fetching && <FolderIconButtonContainer data-test-id="team-folder-icon-button-container">
                {isDefault && permissions.write.permissions &&
                    <FolderIconButton icon="settings" visible={isMouseOver} onClick={handleOpenAllTeamMembersFolderSettings} />
                }
                {!isDefault && (permissions.write.object || permissions.write.permissions || permissions.owner) &&
                    <Popup>
                        <PopupPreview>
                            <FolderIconButton icon="more_vert" visible={isMouseOver} data-test-id="team-folder-more-vert-button" />
                        </PopupPreview>
                        <PopupContent>
                            {(closePopup: () => void) => (
                                <>
                                    {permissions.write.permissions && <PopupMenuItem divider onClick={handleShareFolder(closePopup)}>
                                        <Icon>edit</Icon>
                                        Share Folder...
                                    </PopupMenuItem>}
                                    {permissions.write.object && <PopupMenuItem divider onClick={handleRenameFolder(closePopup)}>
                                        <Icon>label</Icon>
                                        Rename...
                                    </PopupMenuItem>}
                                    {permissions.owner && <PopupMenuItem onClick={handleDeleteFolder(closePopup)}>
                                        <Icon>delete</Icon>
                                        Delete Folder
                                    </PopupMenuItem>}
                                </>
                            )}
                        </PopupContent>
                    </Popup>
                }
            </FolderIconButtonContainer>}
        </ListLine>
    );
}

function TeamFoldersPane({ resourceFolders }: WorkspaceControllerState) {
    const [allResourceFolders, setAllResourceFolders] = useState<IWorkspaceResourceFolder[]>([]);

    useEffect(() => {
        (async () => {
            try {
                const resourceFolders = await workspacesApi.getWorkspaceResourceFolders({
                    workspaceId: WorkspaceController.workspaceId,
                    includeImplicitPermissions: true
                });
                setAllResourceFolders(resourceFolders.filter(folder => !folder.parentFolderId && !isLegacyUserFolder(folder)));
            } catch (err) {
                logger.error(err, "[TeamFoldersPane] Failed to fetch all resource folders");
            }
        })();
    }, []);

    const handleCreateFolder = async () => {
        const name = await ShowInputDialog({
            title: "Create Team Folder",
            maxChars: 50
        });

        if (!name) {
            if (name === "") {
                // @ts-ignore
                ShowWarningDialog({
                    title: "Unable to create folder",
                    message: "Please enter a folder name.",
                });
            }
            return;
        }

        try {
            await withProgressDialog({
                title: "Creating folder...",
                action: async () => {
                    const { id } = await WorkspaceController.createWorkspaceResourceFolder({ name, resourcesType: WorkspaceResourceType.PRESENTATION });
                    ShowDialog(ManageTeamFolderMembersDialog, {
                        folderId: id
                    });
                }
            });
        } catch (err) {
            logger.error(err, "Failed to create team folder");

            ShowErrorDialog({
                title: "Failed to create team folder",
                message: "An error occurred while creating the folder. Please try again."
            });
        }
    };

    const allTeamMembersFolder = resourceFolders.find(isLegacyAllTeamMembersFolder);
    const teamFolders = resourceFolders.filter(folder => !folder.parentFolderId && !isLegacyUserFolder(folder) && !isLegacyAllTeamMembersFolder(folder))
        .sort((a, b) => a.name.localeCompare(b.name));

    const foldersWhereUserHasNoMembership = allResourceFolders.filter(folder => !resourceFolders.some(f => f.id === folder.id));

    return (
        <UIPane>
            <SearchBarContainer className="search-container">
                <Breadcrumb>Team Folders</Breadcrumb>
                <RoundIconButton onClick={handleCreateFolder} data-test-id="team-folders-create-folder-button"><Icon>add</Icon></RoundIconButton>
            </SearchBarContainer>
            <UIPaneResultsContainer>
                <List gridTemplateColumns="2fr 1fr 1fr 50px">
                    <ListHeaderLine>
                        <div>Folder</div>
                        <div>Members</div>
                        <div>Presentations</div>
                    </ListHeaderLine>
                    {[allTeamMembersFolder, ...teamFolders]
                        .map(folder => (
                            <Folder key={folder.id} folder={folder} />
                        ))}
                    {foldersWhereUserHasNoMembership.length > 0 && <NoMembershipDivider>Folders where you are not a member (view only)</NoMembershipDivider>}
                    {foldersWhereUserHasNoMembership.map(folder => (
                        <Folder key={folder.id} folder={folder} />
                    ))}
                </List>
            </UIPaneResultsContainer>
        </UIPane>
    );
}

export default WorkspaceController.withInitializedState(TeamFoldersPane);
